// BANCA_v1 — 🏦 Banca dentro la Cassetta: estratto conto XML (CBI / camt.053), letto senza IA
// BANCA_v2 — abbinamento dei movimenti a fatture, incassi, buste paga, F24 e categorie
import React, { useState, useEffect, useRef } from "react";
import { preparaContesto, proponi, CATEGORIE_BANCA, chiaveControparte, lordoPag, somiglia, iso as isoData } from "./bancaAbbina";
import { statoFatture, aggiungiVendite, gruppiVendite, numeroCausale } from "./venditeCalcoli"; // VENDITE_v1

const C = {
  bg: "#0f1117", surface: "#1a1d27", surface2: "#21253a", border: "#2a2e45", accent: "#f5a623", accentSoft: "#f5a62322",
  text: "#e8eaf2", muted: "#7b82a0", dim: "#4a5068", ok: "#3ecf8e", err: "#f06060", info: "#4e9af5",
};
const MESI_LUNGHI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
// ordine e colore dei tipi di movimento
const TIPI = [
  ["Incasso", C.ok], ["Altra entrata", C.ok],
  ["Stipendio", C.info], ["F24", C.info], ["Cassa edile", C.info],
  ["RIBA", C.accent], ["Addebito SEPA", C.accent], ["Bonifico", C.accent], ["Carta (POS)", C.accent],
  ["Rata mutuo", "#a78bfa"], ["Interessi e competenze", "#a78bfa"], ["Commissioni e canoni", "#a78bfa"], ["Imposta di bollo", "#a78bfa"],
  ["PagoPA / CBILL", C.err], ["Ritenuta sui bonifici dei clienti", C.muted], ["Altro", C.muted],
];
const colTipo = t => (TIPI.find(x => x[0] === t) || [0, C.muted])[1];
const ordTipo = t => { const i = TIPI.findIndex(x => x[0] === t); return i < 0 ? 99 : i; };
const SPIEGA = {
  "Ritenuta sui bonifici dei clienti": "La banca trattiene l'11% sui bonifici per i lavori con detrazione fiscale: non è un costo, è un credito che il commercialista recupera sulle tasse.",
  "Rata mutuo": "Solo gli interessi (e le spese) sono un costo: il capitale è il debito che restituisci.",
  "PagoPA / CBILL": "Pagamenti con avviso PagoPA: se sono all'Agenzia Entrate-Riscossione sono rate di cartelle (tasse).",
};

function tok() { try { return localStorage.getItem("bertoc_jwt") || ""; } catch (e) { return ""; } }
async function api(url, opts) {
  const o = opts || {};
  const h = { Authorization: "Bearer " + tok() };
  const json = o.body && !(o.body instanceof FormData);
  if (json) h["Content-Type"] = "application/json";
  const r = await fetch(url, { method: o.method || "GET", headers: h, body: json ? JSON.stringify(o.body) : o.body });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || ("Errore " + r.status));
  return j;
}
const euro = n => (n < 0 ? "− " : "") + "€ " + Math.abs(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dataIt = s => /^\d{4}-\d{2}-\d{2}/.test(s || "") ? s.slice(8, 10) + "/" + s.slice(5, 7) + "/" + s.slice(0, 4) : (s || "");
const nomeMese = m => /^\d{4}-\d{2}$/.test(m || "") ? MESI_LUNGHI[parseInt(m.slice(5, 7), 10) - 1] + " " + m.slice(0, 4) : (m || "");

const st = {
  card: { background: C.surface, border: "1px solid " + C.border, borderRadius: 12, padding: 16, marginBottom: 14 },
  titolo: { fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 15, marginBottom: 10 },
  nota: { fontSize: 12, color: C.muted, lineHeight: 1.55 },
  inp: { padding: "8px 10px", fontSize: 15, borderRadius: 8, border: "1px solid " + C.border, background: C.bg, color: C.text, outline: "none", fontFamily: "inherit" },
  btn: primo => ({ padding: "9px 14px", borderRadius: 9, border: "1px solid " + (primo ? C.accent : C.border), background: primo ? C.accent : C.surface, color: primo ? "#1a1d27" : C.text, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }),
  chip: (on, col) => ({ padding: "5px 10px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600, border: "1px solid " + (on ? col : C.border), background: on ? col + "22" : C.bg, color: on ? col : C.muted, userSelect: "none", whiteSpace: "nowrap" }),
};

export default function BancaMovimenti({ cantieriDB = [] }) {
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState("");
  const [avviso, setAvviso] = useState("");
  const [lavoro, setLavoro] = useState(false);
  const [mese, setMese] = useState("");
  const [tipo, setTipo] = useState("");
  const [cerca, setCerca] = useState("");
  const [aperto, setAperto] = useState("");
  const [vediCar, setVediCar] = useState(false);
  const [vista, setVista] = useState("tutti");
  const [altri, setAltri] = useState({ documenti: [], pagamenti: [], lavoratori: [], regole: {} });
  const [form, setForm] = useState({});
  const [occupato, setOccupato] = useState(false);
  const [rico, setRico] = useState(null);
  const [ricoN, setRicoN] = useState(null);
  const fileRef = useRef(null);

  async function ricarica() {
    try { setDati(await api("/api/banca/movimenti")); setErrore(""); }
    catch (e) { setErrore(e.message); setDati({ movimenti: [], saldi: {}, caricamenti: [] }); }
    const a = {};
    try { a.documenti = (await api("/api/cassetta/documenti")).documenti || []; } catch (e) { a.documenti = []; }
    try { const pg = await api("/api/v2/pagamenti"); a.pagamenti = (Array.isArray(pg) ? pg : []).map(p => ({ ...p, id: p.id || p._id })); } catch (e) { a.pagamenti = []; }
    try { a.lavoratori = ((await api("/api/lavoratori")).lavoratori || []).map(l => ({ id: l.id, nome: (l.nome_completo || ((l.nome || "") + " " + (l.cognome || ""))).trim() })); } catch (e) { a.lavoratori = []; }
    try { a.regole = (await api("/api/banca/regole")).regole || {}; } catch (e) { a.regole = {}; }
    try { a.vendite = (await api("/api/vendite")).fatture || []; } catch (e) { a.vendite = []; } // VENDITE_v1
    setAltri(a);
  }
  async function abbina(m, abb, regola) {
    setOccupato(true);
    try { await api("/api/banca/movimenti/" + m.id, { method: "PATCH", body: { abb, regola } }); await ricarica(); }
    catch (e) { alert(e.message); }
    setOccupato(false);
  }
  async function confermaSicure(lista) {
    if (!window.confirm("Confermare " + lista.length + " abbinamenti sicuri di questo mese?\n\nLe fatture e le buste abbinate risultano pagate. Ogni abbinamento si può annullare.")) return;
    setOccupato(true);
    try { const j = await api("/api/banca/abbina", { method: "POST", body: { lista } }); setAvviso("✓ " + j.fatti + " movimenti abbinati" + (j.errori.length ? "\n⚠️ " + j.errori.join("\n⚠️ ") : "")); await ricarica(); }
    catch (e) { alert(e.message); }
    setOccupato(false);
  }
  const setF = (id, k, v) => setForm(f => ({ ...f, [id]: { ...(f[id] || {}), [k]: v } }));
  useEffect(() => { ricarica(); }, []);

  async function carica(lista) {
    const files = Array.from(lista || []);
    if (!files.length) return;
    setLavoro(true); setAvviso(""); setErrore("");
    const esiti = [];
    for (const f of files) {
      try {
        const fd = new FormData(); fd.append("file", f, f.name);
        const j = await api("/api/banca/carica", { method: "POST", body: fd });
        esiti.push("✓ " + f.name + ": " + j.nuovi + " movimenti nuovi" + (j.doppi ? ", " + j.doppi + " già presenti" : "") + (j.dal ? " (dal " + dataIt(j.dal) + " al " + dataIt(j.al) + ")" : ""));
        if (j.dal) setMese(j.dal.slice(0, 7));
      } catch (e) { esiti.push("⚠️ " + f.name + ": " + e.message); }
    }
    setAvviso(esiti.join("\n"));
    setLavoro(false);
    ricarica();
  }
  async function togliCaricamento(c) {
    if (!window.confirm("Togliere i movimenti caricati da " + (c.nomeFile || "questo file") + " (" + dataIt(c.dal) + " – " + dataIt(c.al) + ")?\n\nSi tolgono solo i movimenti di quel caricamento. Il file resta salvato sul NAS.")) return;
    try { const j = await api("/api/banca/caricamento/" + c.id, { method: "DELETE" }); setAvviso("✓ Tolti " + j.tolti + " movimenti"); ricarica(); }
    catch (e) { alert(e.message); }
  }

  if (!dati) return <div style={{ ...st.card, color: C.muted }}>Carico i movimenti della banca…</div>;
  const movs = dati.movimenti || [];
  const mesi = Array.from(new Set(movs.map(m => String(m.data || "").slice(0, 7)).filter(Boolean))).sort().reverse();
  // di base l'ultimo mese; se ha solo pochi movimenti (coda dell'export) quello prima
  const quanti = m => movs.filter(x => String(x.data || "").startsWith(m)).length;
  const meseSel = mesi.includes(mese) ? mese : (mesi.length > 1 && quanti(mesi[0]) < 10 ? mesi[1] : (mesi[0] || ""));
  const delMese = movs.filter(m => String(m.data || "").startsWith(meseSel));
  const ctx = preparaContesto({ movimenti: movs, documenti: altri.documenti, pagamenti: altri.pagamenti, cantieri: cantieriDB, lavoratori: altri.lavoratori, regole: altri.regole, vendite: altri.vendite || [] });
  const statoVend = statoFatture(altri.vendite || [], altri.pagamenti || []); // VENDITE_v1
  const prop = {};
  delMese.forEach(m => { if (!m.abb) prop[m.id] = aggiungiVendite(proponi(m, ctx), m, ctx, statoVend, { data: dataIt }); }); // VENDITE_v1
  // RICOLLEGA_BANCA_v1: cerca su tutti i mesi solo quando si preme il pulsante (il calcolo pesa)
  function cercaRicollega() {
    const usati = {}, lista = gruppiVendite(movs, ctx, statoVend);
    lista.forEach(x => { usati["m" + x.m.id] = true; });
    movs.slice().sort((a, b) => String(a.data).localeCompare(String(b.data))).forEach(m => {
      const daCategoria = !!(m.abb && m.abb.tipo === "categoria");
      if (m.abb && !daCategoria) return;
      if (daCategoria && !(m.importo < 0)) return;
      if (usati["m" + m.id]) return;
      const libero = { ...m, abb: null };
      const s = (aggiungiVendite(proponi(libero, ctx), libero, ctx, statoVend, { data: dataIt }) || {}).sicura;
      if (!s) return;
      if (daCategoria && s.tipo !== "documento") return;
      if (s.tipo === "categoria" && ["Stipendio", "Cassa edile", "F24"].includes(m.tipo)) return;
      if (s.id) {
        const d = (altri.documenti || []).find(x => x.id === s.id);
        const ripetibile = d && (/busta/i.test(d.tipo || "") || (d.scadenze || []).length > 1);
        if (usati[s.id] && !ripetibile) return;
        usati[s.id] = true;
      }
      lista.push({ m, abb: s, daCategoria });
    });
    setRico(lista.sort((a, b) => String(a.m.data).localeCompare(String(b.m.data))));
  }
  async function applicaRicollega() {
    setOccupato(true); setAvviso("");
    let fatti = 0; const err = [];
    for (let i = 0; i < rico.length; i++) {
      setRicoN(i + 1);
      try { await api("/api/banca/movimenti/" + rico[i].m.id, { method: "PATCH", body: { abb: rico[i].abb } }); fatti++; }
      catch (e) { err.push(dataIt(rico[i].m.data) + " " + (rico[i].m.controparte || "") + ": " + e.message); }
    }
    setRico(null); setRicoN(null);
    setAvviso("✓ " + fatti + " abbinamenti fatti" + (err.length ? "\n⚠️ " + err.join("\n⚠️ ") : ""));
    await ricarica();
    setOccupato(false);
  }
  const nFatti = delMese.filter(m => m.abb).length;
  const sicure = delMese.filter(m => !m.abb && prop[m.id] && prop[m.id].sicura);
  const daGuardare = delMese.filter(m => !m.abb && !(prop[m.id] && prop[m.id].sicura));
  const q = cerca.trim().toLowerCase();
  const visibili = delMese.filter(m => (!tipo || m.tipo === tipo) && (!q || [m.controparte, m.causale, m.testo, m.tipo].join(" ").toLowerCase().includes(q))
    && (vista === "tutti" || (vista === "fatti" ? !!m.abb : vista === "sicure" ? (!m.abb && prop[m.id] && prop[m.id].sicura) : (!m.abb && !(prop[m.id] && prop[m.id].sicura)))));
  const nomeCant = id => { const c = cantieriDB.find(x => (x._id || x.id) === id); return c ? c.nome : ""; };
  const cantOrd = cantieriDB.slice().sort((a, b) => (a.completato ? 1 : 0) - (b.completato ? 1 : 0) || String(a.nome || "").localeCompare(String(b.nome || "")));
  const entrate = delMese.filter(m => m.importo > 0).reduce((s, m) => s + m.importo, 0);
  const uscite = delMese.filter(m => m.importo < 0).reduce((s, m) => s + m.importo, 0);
  const perTipo = {};
  delMese.forEach(m => { const t = m.tipo || "Altro"; perTipo[t] = perTipo[t] || { n: 0, tot: 0 }; perTipo[t].n++; perTipo[t].tot += m.importo; });
  const tipiMese = Object.keys(perTipo).sort((a, b) => ordTipo(a) - ordTipo(b));
  // saldo a fine mese: l'ultimo saldo contabile del mese, per ogni conto
  const saldoFine = Object.entries(dati.saldi || {}).map(([iban, s]) => {
    const giorni = Object.keys(s).filter(d => d.startsWith(meseSel)).sort();
    return giorni.length ? { iban, data: giorni[giorni.length - 1], saldo: s[giorni[giorni.length - 1]] } : null;
  }).filter(Boolean);
  const interessiMutuo = delMese.filter(m => m.quote).reduce((s, m) => s + (m.quote.interessi || 0) + (m.quote.spese || 0), 0);
  const capitaleMutuo = delMese.filter(m => m.quote).reduce((s, m) => s + (m.quote.capitale || 0), 0);

  return (
    <div>
      <div style={{ ...st.card, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={st.titolo}>🏦 Estratto conto</div>
          <div style={st.nota}>Dall'home banking: Movimenti → Esporta in <b>XML</b>, un mese alla volta (o più file). Si legge qui sul NAS, senza IA. I movimenti già caricati si riconoscono e non si contano due volte.</div>
        </div>
        <button style={st.btn(true)} disabled={lavoro} onClick={() => fileRef.current && fileRef.current.click()}>{lavoro ? "Leggo…" : "📄 Carica XML della banca"}</button>
        <input ref={fileRef} type="file" multiple accept=".xml,text/xml,application/xml" style={{ display: "none" }} onChange={e => { carica(e.target.files); e.target.value = ""; }} />
      </div>
      {avviso && <div style={{ ...st.card, borderColor: /⚠️/.test(avviso) ? C.err : C.ok, whiteSpace: "pre-line", fontSize: 13 }}>{avviso}</div>}
      {errore && <div style={{ ...st.card, borderColor: C.err, color: C.err, fontSize: 13 }}>⚠️ {errore}</div>}

      {movs.length === 0 ? (
        <div style={{ ...st.card, ...st.nota }}>Nessun movimento caricato. Carica l'XML di un mese per cominciare.</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
            <select style={st.inp} value={meseSel} onChange={e => { setMese(e.target.value); setTipo(""); setAperto(""); }}>
              {mesi.map(m => <option key={m} value={m}>{nomeMese(m)}</option>)}
            </select>
            <input style={{ ...st.inp, flex: 1, minWidth: 160 }} placeholder="Cerca nome, causale…" value={cerca} onChange={e => setCerca(e.target.value)} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 14 }}>
            {[["Entrate", euro(entrate), C.ok], ["Uscite", euro(uscite), C.err], ["Differenza", euro(entrate + uscite), entrate + uscite >= 0 ? C.ok : C.err]].concat(
              saldoFine.map(s => ["Saldo al " + dataIt(s.data), euro(s.saldo), s.saldo >= 0 ? C.text : C.err])
            ).map(([l, v, col]) => (
              <div key={l} style={{ background: C.bg, border: "1px solid " + C.border, borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>{l}</div>
                <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 18, color: col, marginTop: 3, whiteSpace: "nowrap" }}>{v}</div>
              </div>
            ))}
          </div>

          <div style={st.card}>
            <div style={st.titolo}>🔁 Tutti i mesi insieme</div>
            {!rico && <>
              <div style={{ ...st.nota, marginBottom: 10 }}>Trova i movimenti rimasti con la sola categoria che ora hanno il loro documento e le proposte sicure ancora da confermare, in tutti i mesi. Prima di fare qualcosa ti mostra l'elenco.</div>
              <button style={st.btn(false)} disabled={occupato} onClick={cercaRicollega}>🔍 Cerca in tutti i mesi</button>
            </>}
            {rico && rico.length === 0 && <div style={st.nota}>Non c'è niente da sistemare in automatico. <span onClick={() => setRico(null)} style={{ color: C.accent, cursor: "pointer" }}>Chiudi</span></div>}
            {rico && rico.length > 0 && <>
              <div style={{ ...st.nota, marginBottom: 8 }}>{rico.filter(x => x.daCategoria).length} da ricollegare al documento · {rico.filter(x => !x.daCategoria).length} proposte sicure nuove. Controlla l'elenco: ogni abbinamento si può annullare.</div>
              <div style={{ maxHeight: 340, overflowY: "auto", border: "1px solid " + C.border, borderRadius: 8, padding: "0 10px", marginBottom: 10 }}>
                {rico.map(x => (
                  <div key={x.m.id} style={{ display: "flex", gap: 10, padding: "6px 0", borderBottom: "1px solid " + C.border + "66", fontSize: 13 }}>
                    <span style={{ color: C.muted, whiteSpace: "nowrap" }}>{dataIt(x.m.data)}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>{x.m.controparte || x.m.tipo}<div style={{ fontSize: 12, color: x.daCategoria ? C.info : C.muted }}>{x.abb.etichetta || x.abb.categoria}</div></span>
                    <b style={{ whiteSpace: "nowrap" }}>{euro(x.m.importo)}</b>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                {ricoN ? <span style={{ color: C.accent, fontWeight: 700 }}>⏳ {ricoN} di {rico.length}… lascia la pagina aperta</span> : <>
                  <button style={st.btn(true)} disabled={occupato} onClick={applicaRicollega}>✓ Conferma {rico.length} abbinamenti</button>
                  <button style={st.btn(false)} disabled={occupato} onClick={() => setRico(null)}>Annulla</button>
                </>}
              </div>
            </>}
          </div>

          <div style={{ ...st.card, borderColor: daGuardare.length ? C.border : C.ok }}>
            <div style={st.titolo}>Abbinamento di {nomeMese(meseSel)}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              {[["tutti", "Tutti " + delMese.length, C.accent], ["fatti", "✓ Sistemati " + nFatti, C.ok], ["sicure", "💡 Proposte sicure " + sicure.length, C.info], ["guardare", "❓ Da guardare " + daGuardare.length, C.err]].map(([id, l, col]) => (
                <span key={id} onClick={() => setVista(id)} style={st.chip(vista === id, col)}>{l}</span>
              ))}
            </div>
            {sicure.length > 0 && <button style={st.btn(true)} disabled={occupato} onClick={() => confermaSicure(sicure.map(m => ({ id: m.id, abb: prop[m.id].sicura })))}>✓ Conferma le {sicure.length} proposte sicure</button>}
            <div style={{ ...st.nota, marginTop: 8 }}>
              Sicure: commissioni, mutui, ritenute, stipendi col nome del lavoratore, fatture con stesso fornitore e importo. Le altre si guardano una per una: tocca il movimento.
              {daGuardare.some(m => prop[m.id] && /Fattura non trovata|F24 non/.test(prop[m.id].nota)) ? " Molte uscite aspettano la fattura: caricala nella cassetta e qui l'abbinamento compare da solo." : ""}
            </div>
          </div>

          <div style={st.card}>
            <div style={st.titolo}>{nomeMese(meseSel)} per tipo</div>
            {tipiMese.map(t => (
              <div key={t} onClick={() => setTipo(tipo === t ? "" : t)} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "6px 0", borderTop: "1px solid " + C.border, cursor: "pointer", fontSize: 14, background: tipo === t ? C.accentSoft : "transparent" }}>
                <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: colTipo(t), marginRight: 8 }} />{t} <span style={{ color: C.dim, fontSize: 12 }}>· {perTipo[t].n}</span></span>
                <b style={{ color: perTipo[t].tot >= 0 ? C.ok : C.text, whiteSpace: "nowrap" }}>{euro(perTipo[t].tot)}</b>
              </div>
            ))}
            {capitaleMutuo > 0 && <div style={{ ...st.nota, marginTop: 8 }}>Mutui: {euro(capitaleMutuo)} di capitale restituito (non è un costo) e {euro(interessiMutuo)} di interessi e spese (costo).</div>}
            {perTipo["Ritenuta sui bonifici dei clienti"] && <div style={{ ...st.nota, marginTop: 4 }}>Ritenute sui bonifici dei clienti: {euro(-perTipo["Ritenuta sui bonifici dei clienti"].tot)} di credito d'imposta, non un costo.</div>}
            <div style={{ ...st.nota, marginTop: 8 }}>Tocca un tipo per vedere solo quei movimenti.</div>
          </div>

          <div style={st.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
              <div style={st.titolo}>Movimenti {tipo ? "· " + tipo : ""} ({visibili.length})</div>
              {tipo && <span onClick={() => setTipo("")} style={{ ...st.chip(true, C.accent) }}>✕ tutti i tipi</span>}
            </div>
            {visibili.length === 0 && <div style={st.nota}>Nessun movimento.</div>}
            {visibili.map(m => (
              <div key={m.id} style={{ borderTop: "1px solid " + C.border, padding: "8px 0" }}>
                <div onClick={() => setAperto(aperto === m.id ? "" : m.id)} style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
                  <div style={{ fontSize: 12, color: C.muted, width: 44, flexShrink: 0, paddingTop: 2 }}>{dataIt(m.data).slice(0, 5)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.controparte || m.tipo}</div>
                    <div style={{ fontSize: 12, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <span style={{ color: colTipo(m.tipo) }}>{m.tipo}</span>{m.causale ? " · " + m.causale : ""}{m.mese ? " · rif. " + nomeMese(m.mese) : ""}
                    </div>
                    <div style={{ fontSize: 12, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      color: m.abb ? C.ok : prop[m.id] && prop[m.id].sicura ? C.info : prop[m.id] && prop[m.id].candidati.length ? C.accent : C.err }}>
                      {m.abb ? "✓ " + (m.abb.etichetta || m.abb.categoria)
                        : prop[m.id] && prop[m.id].sicura ? "💡 " + prop[m.id].sicura.etichetta
                        : prop[m.id] && prop[m.id].candidati.length ? "❓ forse: " + prop[m.id].candidati[0].testo
                        : "❓ " + ((prop[m.id] && prop[m.id].nota) || "da sistemare")}
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, whiteSpace: "nowrap", color: m.importo >= 0 ? C.ok : C.text }}>{euro(m.importo)}</div>
                </div>
                {aperto === m.id && (
                  <div style={{ margin: "8px 0 4px 54px", fontSize: 12, color: C.muted, lineHeight: 1.55 }}>
                    {m.quote && <div style={{ color: C.text, marginBottom: 4 }}>Capitale {euro(m.quote.capitale)} · interessi {euro(m.quote.interessi)}{m.quote.spese ? " · spese " + euro(m.quote.spese) : ""}</div>}
                    {SPIEGA[m.tipo] && <div style={{ color: C.text, marginBottom: 4 }}>{SPIEGA[m.tipo]}</div>}
                    <div>Contabile {dataIt(m.data)} · valuta {dataIt(m.valuta)} · codice {m.codice || "—"}</div>
                    <div style={{ marginTop: 4, wordBreak: "break-word" }}>{m.testo}</div>
                    <Abbinamento m={m} p={prop[m.id]} f={form[m.id] || {}} setF={(k, v) => setF(m.id, k, v)} abbina={abbina} occupato={occupato}
                      cantOrd={cantOrd} nomeCant={nomeCant} pagamenti={altri.pagamenti} documenti={altri.documenti} movs={movs} vendite={altri.vendite} statoVend={statoVend} />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={st.card}>
            <div onClick={() => setVediCar(!vediCar)} style={{ ...st.titolo, marginBottom: 0, cursor: "pointer" }}>{vediCar ? "▾" : "▸"} File caricati ({(dati.caricamenti || []).length})</div>
            {vediCar && (dati.caricamenti || []).map(c => (
              <div key={c.id} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", borderTop: "1px solid " + C.border, padding: "7px 0", fontSize: 13, marginTop: 6 }}>
                <span style={{ flex: 1, minWidth: 160 }}>{c.nomeFile || "file"} <span style={{ color: C.muted }}>· {dataIt(c.dal)} – {dataIt(c.al)} · {c.nuovi} movimenti</span></span>
                <span style={{ color: C.dim, fontSize: 12 }}>caricato il {dataIt(String(c.il || "").slice(0, 10))}</span>
                <button style={{ ...st.btn(false), padding: "5px 10px", fontSize: 12 }} onClick={() => togliCaricamento(c)}>🗑 Togli</button>
              </div>
            ))}
          </div>
          <div style={{ ...st.nota, marginBottom: 20 }}>Le fatture e le buste abbinate risultano pagate nella cassetta; gli incassi registrati qui entrano nei pagamenti del cantiere. Ogni abbinamento si può annullare.</div>
        </>
      )}
    </div>
  );
}

// ABBINA_BUSTA_v1: scelta a mano della busta paga di un bonifico, con quanto è già stato pagato
function SceltaBusta({ m, f, setF, abbina, occupato, documenti, movs }) {
  const pagato = {};
  (movs || []).forEach(x => { if (x.abb && x.abb.tipo === "documento" && x.abb.id) pagato[x.abb.id] = (pagato[x.abb.id] || 0) + Math.abs(Number(x.importo) || 0); });
  const nome = d => d.lavoratoreNome || d.fornitore || "";
  const netto = d => Math.abs(Number(d.netto !== null && d.netto !== undefined ? d.netto : d.totale) || 0);
  const testo = (m.controparte || "") + " " + (m.causale || "");
  const buste = (documenti || []).filter(d => /busta/i.test(d.tipo || "")).map(d => ({ d, s: somiglia(testo, nome(d)) }));
  const top = buste.reduce((a, x) => Math.max(a, x.s), 0);
  const riferimento = Date.parse((m.mese || String(m.data || "").slice(0, 7)) + "-15");
  const distanza = d => Math.abs(Date.parse((d.mese || String(d.data || "").slice(0, 7)) + "-15") - riferimento);
  const ordina = (a, b) => distanza(a.d) - distanza(b.d) || nome(a.d).localeCompare(nome(b.d));
  const sue = top >= 0.5 ? buste.filter(x => x.s === top).sort(ordina) : [];
  const altre = buste.filter(x => !sue.includes(x)).sort((a, b) => String(b.d.mese || "").localeCompare(String(a.d.mese || "")) || nome(a.d).localeCompare(nome(b.d)));
  if (!buste.length) return null;
  const scelta = f.busta !== undefined ? f.busta : (sue.length ? sue[0].d.id : "");
  const d = buste.map(x => x.d).find(x => x.id === scelta);
  const voce = x => nomeMese(x.mese) + " · " + nome(x) + " · netto " + euro(netto(x)) + (pagato[x.id] ? " · pagato " + euro(pagato[x.id]) : "");
  const dopo = d ? (pagato[d.id] || 0) + Math.abs(Number(m.importo) || 0) : 0;
  const diff = d ? Math.round((netto(d) - dopo) * 100) / 100 : 0;
  return (
    <div style={{ borderTop: "1px solid " + C.border, paddingTop: 8, marginTop: 8 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>È lo stipendio di una busta paga?</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select style={{ ...st.inp, fontSize: 13, flex: 1, minWidth: 200, maxWidth: "100%" }} value={scelta} onChange={e => setF("busta", e.target.value)}>
          <option value="">— scegli la busta —</option>
          {sue.length > 0 && <optgroup label="Di questa persona">{sue.map(x => <option key={x.d.id} value={x.d.id}>{voce(x.d)}</option>)}</optgroup>}
          {altre.length > 0 && <optgroup label="Tutte le altre">{altre.map(x => <option key={x.d.id} value={x.d.id}>{voce(x.d)}</option>)}</optgroup>}
        </select>
        <button style={{ ...st.btn(true), padding: "6px 12px", fontSize: 13 }} disabled={occupato || !d}
          onClick={() => abbina(m, { tipo: "documento", id: d.id, etichetta: "Busta paga " + nome(d) + " " + nomeMese(d.mese) })}>Abbina alla busta</button>
      </div>
      {d && <div style={{ fontSize: 12, marginTop: 6, color: diff === 0 ? C.ok : C.accent }}>
        Con questo bonifico: pagati {euro(dopo)} su {euro(netto(d))}{diff === 0 ? " · pari" : diff > 0 ? " · restano " + euro(diff) : " · pagati " + euro(-diff) + " in più"}
      </div>}
    </div>
  );
}

// INCASSI_GRUPPI_v1: scelta a mano della fattura di vendita di un incasso, anche con più bonifici
function SceltaFattura({ m, f, setF, abbina, occupato, vendite, statoVend }) {
  const num = numeroCausale(m);
  const testo = (m.controparte || "") + " " + (m.causale || "");
  const lista = (vendite || []).filter(v => { const s = statoVend.perId[v.id]; return s && !["nota", "stornata"].includes(s.stato) && s.banca < s.atteso - 1; })
    .map(v => ({ v, s: statoVend.perId[v.id], punti: (num && String(parseInt(v.numero, 10)) === num ? 2 : 0) + somiglia(testo, v.cliente) }))
    .sort((a, b) => b.punti - a.punti || String(b.v.data).localeCompare(String(a.v.data)));
  if (!lista.length) return null;
  const scelta = f.fattura !== undefined ? f.fattura : (lista[0].punti >= 0.5 ? lista[0].v.id : "");
  const x = lista.find(y => y.v.id === scelta);
  const dopo = x ? x.s.banca + m.importo : 0;
  const diff = x ? Math.round((x.s.atteso - dopo) * 100) / 100 : 0;
  return (
    <div style={{ borderTop: "1px solid " + C.border, paddingTop: 8, marginTop: 8 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>È il pagamento di una fattura di vendita?</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select style={{ ...st.inp, fontSize: 13, flex: 1, minWidth: 200, maxWidth: "100%" }} value={scelta} onChange={e => setF("fattura", e.target.value)}>
          <option value="">— scegli la fattura —</option>
          {lista.map(y => <option key={y.v.id} value={y.v.id}>{"n. " + y.v.numero + " · " + y.v.cliente + " · " + dataIt(y.v.data) + " · " + euro(y.s.atteso)}</option>)}
        </select>
        <button style={{ ...st.btn(true), padding: "6px 12px", fontSize: 13 }} disabled={occupato || !x}
          onClick={() => abbina(m, { tipo: "vendita", id: x.v.id, etichetta: "Fattura n. " + x.v.numero + " " + x.v.cliente, creaPagamento: !!x.v.cantiereId && x.s.daPagamenti < m.importo - 1 })}>Abbina alla fattura</button>
      </div>
      {x && <div style={{ fontSize: 12, marginTop: 6, color: Math.abs(diff) <= 1 ? C.ok : C.accent }}>
        Con questo bonifico: arrivati {euro(dopo)} su {euro(x.s.atteso)}{Math.abs(diff) <= 1 ? " · pari" : diff > 0 ? " · restano " + euro(diff) : " · arrivati " + euro(-diff) + " in più"}
      </div>}
    </div>
  );
}

// Pannello di abbinamento di un movimento (dentro il dettaglio)
function Abbinamento({ m, p, f, setF, abbina, occupato, cantOrd, nomeCant, pagamenti, documenti, movs, vendite, statoVend }) {
  const box = { marginTop: 10, padding: 12, borderRadius: 10, background: C.surface2, color: C.text, fontSize: 13 };
  if (m.abb) {
    return (
      <div style={box}>
        <div style={{ color: C.ok, fontWeight: 700 }}>✓ {m.abb.etichetta || m.abb.categoria}</div>
        <div style={{ color: C.muted, marginTop: 2 }}>{m.abb.categoria ? "Categoria: " + m.abb.categoria + " · " : ""}{m.abb.auto ? "confermato in automatico" : "scelto a mano"}{m.abb.il ? " il " + dataIt(m.abb.il.slice(0, 10)) : ""}</div>
        <button style={{ ...st.btn(false), marginTop: 8, padding: "6px 10px", fontSize: 12 }} disabled={occupato}
          onClick={() => { if (window.confirm("Annullare questo abbinamento?" + (m.abb.tipo === "vendita" && m.abb.pagamentoCreato ? "\n\nViene tolto anche il pagamento registrato nel cantiere." : "") + (m.abb.tipo === "nuovoPagamento" ? "\n\nIl pagamento registrato nel cantiere resta: se è sbagliato toglilo da Contabilità." : ""))) abbina(m, null); }}>Annulla abbinamento</button>
        {m.abb.tipo === "categoria" && m.importo > 0 && <SceltaFattura m={m} f={f} setF={setF} abbina={abbina} occupato={occupato} vendite={vendite} statoVend={statoVend} />}
        {m.abb.tipo === "categoria" && m.importo < 0 && <SceltaBusta m={m} f={f} setF={setF} abbina={abbina} occupato={occupato} documenti={documenti} movs={movs} />}
      </div>
    );
  }
  const cand = (p && p.candidati) || [];
  const cat = f.cat || (cand.find(c => c.abb.tipo === "categoria") || {}).abb?.categoria || "";
  const cid = f.cid !== undefined ? f.cid : (p && p.cantiereProposto) || "";
  const iva = f.iva !== undefined ? f.iva : 10;
  const giaNelCant = cid ? pagamenti.filter(x => x.cantiereId === cid) : [];
  return (
    <div style={box}>
      {cand.length > 0 && <div style={{ fontWeight: 700, marginBottom: 6 }}>Proposte</div>}
      {cand.map((c, i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", padding: "5px 0", borderTop: i ? "1px solid " + C.border : "none" }}>
          <div style={{ flex: 1, minWidth: 180 }}>{c.testo}<div style={{ fontSize: 11, color: C.muted }}>{c.motivo}</div></div>
          <button style={{ ...st.btn(i === 0), padding: "6px 12px", fontSize: 13 }} disabled={occupato} onClick={() => abbina(m, c.abb)}>Abbina</button>
        </div>
      ))}
      {p && p.nota && <div style={{ color: C.accent, margin: "4px 0 8px" }}>{p.nota}</div>}

      {m.importo > 0 && (
        <div style={{ borderTop: "1px solid " + C.border, paddingTop: 8, marginTop: 6 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Registra come incasso di un cantiere</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select style={{ ...st.inp, fontSize: 13, flex: 1, minWidth: 180 }} value={cid} onChange={e => setF("cid", e.target.value)}>
              <option value="">— scegli il cantiere —</option>
              {cantOrd.map(c => <option key={c._id || c.id} value={c._id || c.id}>{c.nome}{c.cliente ? " · " + c.cliente : ""}{c.completato ? " (chiuso)" : ""}</option>)}
            </select>
            <select style={{ ...st.inp, fontSize: 13 }} value={iva} onChange={e => setF("iva", Number(e.target.value))}>
              {[10, 22, 4, 0].map(v => <option key={v} value={v}>IVA {v}%</option>)}
            </select>
            <select style={{ ...st.inp, fontSize: 13 }} value={f.tipoPag || "acconto"} onChange={e => setF("tipoPag", e.target.value)}>
              <option value="acconto">acconto</option><option value="saldo">saldo</option>
            </select>
          </div>
          {cid && (
            <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>
              {euro(m.importo)} con IVA {iva}% = {euro(iva ? m.importo / (1 + iva / 100) : m.importo)} senza IVA.
              {giaNelCant.length ? " Già segnati in " + nomeCant(cid) + ": " + giaNelCant.map(x => dataIt(isoData(x.data)) + " " + euro(lordoPag(x))).join(", ") + " — controlla che questo incasso non sia già compreso." : " Nessun pagamento ancora segnato in questo cantiere."}
            </div>
          )}
          <button style={{ ...st.btn(true), marginTop: 8, padding: "6px 12px", fontSize: 13 }} disabled={occupato || !cid}
            onClick={() => abbina(m, { tipo: "nuovoPagamento", cantiereId: cid, iva, tipoPag: f.tipoPag || "acconto", note: m.causale, etichetta: "Incasso registrato in " + nomeCant(cid) })}>Registra incasso</button>
        </div>
      )}

      {m.importo > 0 && <SceltaFattura m={m} f={f} setF={setF} abbina={abbina} occupato={occupato} vendite={vendite} statoVend={statoVend} />}
      {m.importo < 0 && <SceltaBusta m={m} f={f} setF={setF} abbina={abbina} occupato={occupato} documenti={documenti} movs={movs} />}

      <div style={{ borderTop: "1px solid " + C.border, paddingTop: 8, marginTop: 8 }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Oppure solo la categoria</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <select style={{ ...st.inp, fontSize: 13, flex: 1, minWidth: 180 }} value={cat} onChange={e => setF("cat", e.target.value)}>
            <option value="">— scegli —</option>
            {CATEGORIE_BANCA.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button style={{ ...st.btn(false), padding: "6px 12px", fontSize: 13 }} disabled={occupato || !cat}
            onClick={() => abbina(m, { tipo: "categoria", categoria: cat, etichetta: cat }, f.ricorda && m.controparte ? { chiave: chiaveControparte(m.controparte), categoria: cat } : null)}>Salva</button>
        </div>
        {m.controparte && m.controparte !== "Banca" && (
          <label style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6, color: C.muted, fontSize: 12, textTransform: "none", letterSpacing: 0, fontWeight: 400, }}>
            <input type="checkbox" checked={!!f.ricorda} onChange={e => setF("ricorda", e.target.checked)} /> Ricorda: da ora i movimenti di «{m.controparte}» vanno in questa categoria
          </label>
        )}
      </div>
    </div>
  );
}
